import { getOr, isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { IconButton } from '../../shared/button/IconButton';
import { ARROW_WIDTH_OFFSET, Carousel } from '../../shared/carousel/Carousel';
import { Icon } from '../../shared/icon/Icon';
import { ChoiceLeft, ChoiceRight, Delete, Download, Mail, QuestionMark, Tick, WarningSign } from '../../shared/icons';
import { CustomTooltip, IconTooltip } from '../../shared/tooltip';
import { NettingEngineSummary } from '../store/types';
import styles from './NettingEngine.module.scss';
import { NettingEngineAssumptionModal } from './NettingEngineAssumptionModal';
import { NettingEngineJurisdictionResults } from './NettingEngineJurisdictionResults';
import { NettingEngineSectionTenModal } from './NettingEngineSectionTenModal';
import { NettingEngineWhatAreTheseModal } from './NettingEngineWhatAreTheseModal';
import { downloadNettingEngineResultsStarted, getIsCustomConclusionValue, getIsDownloadingNettingEngineSummary, getNettingAssumptions, getNettingAssumptionsModalOpen, GMRAJurisdictionalNettingInformation, ISDAJurisdictionalNettingInformation, getResultsEmailDetails, NettingResponseData, setNettingAssumptionsModalOpen, toggleIndustryOrCustomConclusions, toggleNettingResultsEmailModalOpen } from './store';
import { NettingResultsEmailModal } from './NettingResultsEmailModal';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { fetchAllBasicUsersStarted } from '../../admin/users/store';

const { green, red, amber, darkRed, amethyst, french, yellowGreen, fixedPrimary } = styles;

const RESULT_MARGIN_PADDING_BORDER_OFFSET = 42;
const CAROUSEL_MARKERS_OFFSET = 10;
const CAROUSEL_MARGIN_RIGHT = 10;

const HEIGHT_OFFSET = 116;
const HELP_ICON_WITH_MARGIN = 38;
const WHATS_THIS_WIDTH = 162;

interface NettingEngineResultsProps {
    results: NettingResponseData;
    width: number;
    height: number;
    isInstanceView?: boolean;
}

export const NettingEngineResults: React.FC<NettingEngineResultsProps> = ({ results, width, height, isInstanceView = false }) => {
    const { jurisdictionalInformation, overallSummary, overallClientSummary } = results;

    const sectionTenImpactsDecision: boolean = useMemo(() => getOr(false, 'sectionTenImpactsDecision', results), [results]);

    useFetchStarted([fetchAllBasicUsersStarted()]);
    const dispatch = useAppDispatch();
    const [sectionTenModalOpen, setSectionTenModalOpen] = useState(false);
    const [whatIsThisModalOpen, setWhatIsThisModalOpen] = useState(false);
    const nettingEngineAssumptionsModalOpen = useAppSelector(getNettingAssumptionsModalOpen);
    const nettingEngineAssumptions = useAppSelector(getNettingAssumptions);
    const isCustomConclusion = useAppSelector(getIsCustomConclusionValue);
    const isDownloading = useAppSelector(getIsDownloadingNettingEngineSummary);
    const emailDetails = useAppSelector(getResultsEmailDetails);
    const openNettingAssumptionsModal = useCallback(() => dispatch(setNettingAssumptionsModalOpen(true)), [dispatch]);
    const closeNettingAssumptionsModal = useCallback(() => dispatch(setNettingAssumptionsModalOpen(false)), [dispatch]);
    const openSectionTenModal = useCallback(() => {
        if (sectionTenImpactsDecision) {
            setSectionTenModalOpen(true);
        }
    }, [setSectionTenModalOpen, sectionTenImpactsDecision]);
    const closeSectionTenModal = useCallback(() => setSectionTenModalOpen(false), [setSectionTenModalOpen]);
    const openWhatIsThisModal = useCallback(() => setWhatIsThisModalOpen(true), [setWhatIsThisModalOpen]);
    const closeWhatIsThisModal = useCallback(() => setWhatIsThisModalOpen(false), [setWhatIsThisModalOpen]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const conclusionsButton = useCallback(() => {
        setIsLoading(true);
        // Loading spinner added in for user experience when switching between industry and custom answers. No data is being fetched
        dispatch(toggleIndustryOrCustomConclusions(!isCustomConclusion));
        setTimeout(() => setIsLoading(false), 150);
    }, [dispatch, setIsLoading, isCustomConclusion]);

    const summaryValue = useMemo(() => isCustomConclusion ? overallClientSummary : overallSummary, [isCustomConclusion, overallClientSummary, overallSummary]);
    const showCustomConclusionIcon = useMemo(() => (jurisdictionalInformation as (ISDAJurisdictionalNettingInformation | GMRAJurisdictionalNettingInformation)[]).some(jurisdictionalResult => !isNull(jurisdictionalResult.clientSignOffDetails)), [jurisdictionalInformation]);

    const iconToShow = useMemo(() => {
        switch (summaryValue) {
            case NettingEngineSummary.ISDA_YES:
                return { icon: Tick, color: green };
            case NettingEngineSummary.ISDA_YES_PRODUCTS_UNKNOWN:
                return { icon: Tick, color: yellowGreen };
            case NettingEngineSummary.ISDA_NO:
                return { icon: Delete, color: red };
            case NettingEngineSummary.GMRA_YES_PRODUCTS_UNKNOWN_AND_YES:
                return { color: yellowGreen, icon: Tick };
            case NettingEngineSummary.GMRA_YES_AND_YES:
                return { color: green, icon: Tick };
            case NettingEngineSummary.GMRA_YES_AND_NO:
                return { color: amber, icon: WarningSign };
            case NettingEngineSummary.GMRA_NO_AND_YES:
                return { color: amber, icon: WarningSign };
            case NettingEngineSummary.GMRA_NO_AND_NO:
                return { color: red, icon: Delete };
            case NettingEngineSummary.GMRA_MAYBE_AND_MAYBE:
                return { color: amber, icon: WarningSign };
            case NettingEngineSummary.GMRA_YES_AND_MAYBE:
                return { color: amber, icon: WarningSign };
            case NettingEngineSummary.GMRA_MAYBE_AND_YES:
                return { color: amber, icon: WarningSign };
            case NettingEngineSummary.GMRA_NO_AND_MAYBE:
                return { color: amber, icon: WarningSign };
            case NettingEngineSummary.GMRA_MAYBE_AND_NO:
                return { color: amber, icon: WarningSign };
            default:
                return { icon: WarningSign, color: amber };
        }
    }, [summaryValue]);

    const jurisdictionalInfo = jurisdictionalInformation as (ISDAJurisdictionalNettingInformation | GMRAJurisdictionalNettingInformation)[];

    const markers = useMemo(() => (jurisdictionalInfo).map((_, index) => index), [jurisdictionalInfo]);

    const conclusionsLabel = useMemo(() => isCustomConclusion ? 'View Ark51 Conclusions' : 'View Custom Conclusions', [isCustomConclusion]);
    const conclusionIcon = useMemo(() => isCustomConclusion ? ChoiceLeft : ChoiceRight, [isCustomConclusion]);
    const whatAreTheseText = useMemo(() => (!showCustomConclusionIcon && !sectionTenImpactsDecision) ? '(What is this?)' : '(What are these?)', [showCustomConclusionIcon, sectionTenImpactsDecision]);

    const carouselHeight = useMemo(() => height - HEIGHT_OFFSET, [height]);
    const resultsWidth = useMemo(() => width - (RESULT_MARGIN_PADDING_BORDER_OFFSET + (jurisdictionalInformation.length > 0 ? (2 * ARROW_WIDTH_OFFSET) : 0) - CAROUSEL_MARGIN_RIGHT), [width, jurisdictionalInformation]);
    const resultsHeight = useMemo(() => carouselHeight - (RESULT_MARGIN_PADDING_BORDER_OFFSET + CAROUSEL_MARKERS_OFFSET), [carouselHeight]);

    const svgDimensions = useMemo(() => ({ height: resultsHeight - 30, width: resultsWidth * 0.45 }), [resultsHeight, resultsWidth]);

    const downloadNettingEngine = useCallback(() => {
        const { height, width } = svgDimensions;
        dispatch(downloadNettingEngineResultsStarted(height, width));
    }, [dispatch, svgDimensions]);

    const openEmailDetailsModal = useCallback(() => dispatch(toggleNettingResultsEmailModalOpen(true)), [dispatch]);

    const helpIconsWidth = useMemo(() => {
        let width = (HELP_ICON_WITH_MARGIN * 3) + WHATS_THIS_WIDTH;
        if (sectionTenImpactsDecision) {
            width = width + HELP_ICON_WITH_MARGIN;
        }
        if (showCustomConclusionIcon) {
            width = width + HELP_ICON_WITH_MARGIN;
        }
        return width;
    }, [sectionTenImpactsDecision, showCustomConclusionIcon]);

    return (
        <div className={styles.nettingResultsWrapper}>
            <div className={styles.titleContainer}>
                <div className={styles.title} style={{ width: `calc(100% - ${helpIconsWidth}px)`, left: `${helpIconsWidth / 2}px` }} data-testid='netting-engine-title'>Netting Engine</div>
                <div className={styles.helpIconsWrapper}>
                    <CustomTooltip overlayText='Email' placement='top'>
                        <div className={styles.helpIcon}>
                            <IconButton icon={Mail} fontSize={28} color={french} onClick={openEmailDetailsModal} />
                        </div>
                    </CustomTooltip>
                    <CustomTooltip overlayText='Download' placement='top'>
                        <div className={styles.helpIcon}>
                            <IconButton icon={Download} fontSize={28} color={french} onClick={downloadNettingEngine} showSpinner={isDownloading} disabled={isDownloading} />
                        </div>
                    </CustomTooltip>
                    {sectionTenImpactsDecision ?
                        <div className={styles.helpIcon} onClick={openSectionTenModal}>
                            <IconTooltip icon={WarningSign} fontSize={28} iconColor={darkRed} content='Section 10(a)' cursor='pointer' placement='left' />
                        </div> :
                        null
                    }
                    {showCustomConclusionIcon ?
                        <div className={styles.helpIcon} onClick={conclusionsButton}>
                            <IconTooltip icon={conclusionIcon} fontSize={28} iconColor={french} content={conclusionsLabel} cursor='pointer' placement='top' />
                        </div> :
                        null
                    }
                    <div className={styles.helpIcon} onClick={openNettingAssumptionsModal}>
                        <IconTooltip icon={QuestionMark} fontSize={28} iconColor={amethyst} content='View Assumptions' cursor='pointer' placement='top' />
                    </div>
                    <div className={styles.whatIsThis} onClick={openWhatIsThisModal}>{whatAreTheseText}</div>
                </div>
            </div>
            <div className={styles.nettingSummary}>Overall Analysis:
                <span className={styles.nettingOverallSpan} style={{ color: `${iconToShow.color}` }}>{summaryValue}</span>
                <Icon icon={iconToShow.icon} fontSize={20} color={iconToShow.color} fill={fixedPrimary} />
            </div>
            <div className={styles.resultsCarouselWrapper}>
                <Carousel
                    markers={markers}
                    height={carouselHeight}
                    width={width - (2 * ARROW_WIDTH_OFFSET)}
                >
                    {(jurisdictionalInfo).map((jurisdiction, index) => (
                        <NettingEngineJurisdictionResults key={index} jurisdictionalResults={jurisdiction} viewCustomConclusions={isCustomConclusion} height={resultsHeight} width={resultsWidth} isLoading={isLoading} index={index} isInstanceView={isInstanceView} isSingleJurisdiction={jurisdictionalInformation.length === 1} />
                    ))}
                </Carousel>
            </div>
            <NettingEngineAssumptionModal isOpen={nettingEngineAssumptionsModalOpen} closeModal={closeNettingAssumptionsModal} assumptions={nettingEngineAssumptions} />
            <NettingEngineSectionTenModal isOpen={sectionTenModalOpen} closeModal={closeSectionTenModal} />
            <NettingEngineWhatAreTheseModal isOpen={whatIsThisModalOpen} closeModal={closeWhatIsThisModal} sectionTenImpactsDecision={sectionTenImpactsDecision || false} showCustomConclusionIcon={showCustomConclusionIcon} />
            {!isNull(emailDetails) && <NettingResultsEmailModal emailDetails={emailDetails} svgDimensions={svgDimensions} />}
        </div>
    );
};
